import moment from 'moment';
import * as XLSX from 'xlsx';
import { __t } from '../../i18n/t';

type InvoiceSummaryRow = {
  retailer_id: string | null;
  count: string | number;
  total: number | null;
  totalretailervalue: number | null;
};

export type InvoiceTotalsRow = InvoiceSummaryRow & { isGrandTotal?: boolean };

const MONEY_FORMAT = '"R$" #,##0.00';

/**
 * Linhas do quadro Totais: uma por revenda, da que mais recebe para a que menos recebe, mais a
 * linha "Todos" para o admin. Devolve array novo porque `summaryData` vem do cache do react-query
 * e a exportação lê o mesmo objeto: somar em cima dele contaria o total duas vezes.
 */
export const invoiceTotals = (summaryData: InvoiceSummaryRow[], withGrandTotal: boolean): InvoiceTotalsRow[] => {
  const rows: InvoiceTotalsRow[] = [...summaryData];

  if (withGrandTotal) {
    rows.push(summaryData.reduce<InvoiceTotalsRow>(
      (acc, row) => ({
        ...acc,
        count: Number(acc.count) + Number(row.count),
        total: (acc.total ?? 0) + (row.total ?? 0),
        totalretailervalue: (acc.totalretailervalue ?? 0) + (row.totalretailervalue ?? 0),
      }),
      { retailer_id: null, count: 0, total: 0, totalretailervalue: 0, isGrandTotal: true }
    ));
  }

  return rows.sort((a, b) => (b.totalretailervalue ?? 0) - (a.totalretailervalue ?? 0));
};

/** Os valores seguem como número, para somar no Excel; só a exibição é em reais. */
export const invoiceTotalsWorkbook = (rows: InvoiceTotalsRow[], retailers: Array<{ id: string; name: string }>) => {
  const retailerName = (row: InvoiceTotalsRow) => {
    if (row.isGrandTotal) return __t('Todos');
    if (!row.retailer_id) return __t('Sem Revenda');
    return retailers.find(retailer => retailer.id === row.retailer_id)?.name ?? __t('Não identificado');
  };

  const sheet = XLSX.utils.aoa_to_sheet([
    [__t('Revenda'), __t('Quantidade'), __t('Valor Total'), __t('Valor dos parceiros')],
    ...rows.map(row => [retailerName(row), Number(row.count), row.total ?? 0, row.totalretailervalue ?? 0]),
  ]);

  rows.forEach((_, index) => {
    sheet[`C${index + 2}`].z = MONEY_FORMAT;
    sheet[`D${index + 2}`].z = MONEY_FORMAT;
  });
  sheet['!cols'] = [{ wch: 36 }, { wch: 12 }, { wch: 16 }, { wch: 20 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, __t('Totais'));
  return workbook;
};

/** Sem competência (filtro limpo), o relatório traz todos os meses e o nome não cita período. */
export const invoiceTotalsFileName = ({ startMonth, endMonth }: { startMonth?: string; endMonth?: string }) => {
  if (!startMonth || !endMonth) return 'totais-cobrancas.xlsx';

  const start = moment(startMonth).format('YYYY-MM');
  const end = moment(endMonth).format('YYYY-MM');
  return start === end ? `totais-cobrancas-${start}.xlsx` : `totais-cobrancas-${start}-a-${end}.xlsx`;
};
